import React, { useCallback, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { makeStyles } from '../../../tools/makeStyles';
import { getIconUrl } from '../utils/getIconUrl';
import { RATINGS_SLIDING } from '../enps/constants/constants';
import { ratingsSlidingIcons } from './constants/constants';
import Header from '../common/Header.jsx';
import Footer from '../common/Footer.jsx';
import { defaultDescription, defaultTitle } from '../constants/constants';
import { setTabIndex, buildRatingOptionArialLabel } from '../../helpers/accessibility_helper';
import { useViewport } from '../../helpers/viewport_helper';
import { useNumberKeySelectorCallback } from '../../helpers/number_key_selector_helper';
import { useKeySelect } from '../../helpers/key_select_helper';
import { DEFAULT_TRANSLATION } from '../../helpers/constants';

const MAX_COUNT = 10;
const MAX_WIDTH = 64;
const MIN_WIDTH = 30;

const useStyles = makeStyles(() => ({
  scaleRoot: {
    boxSizing: 'border-box',
    display: 'block',
  },
  scaleContainer: {
    boxSizing: 'border-box',
    display: 'inline-block',
    width: '100%',
  },
  scaleButton: {
    display: 'inline-block',
    maxWidth: MAX_WIDTH,
    width: MAX_WIDTH,
    minWidth: MIN_WIDTH,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    userSelect: 'none',
    textAlign: 'center',
    '&:hover, &:focus': {
      backgroundColor: '#eee',
      color: 'white',
    },
  },
  icon: {
    width: '100%',
    padding: 0,
  },
  labelContainer: {
    width: 'auto',
    justifyContent: 'space-between',
    color: '#4f4f4f',
  },
  maxLabel: {
    textAlign: 'right',
  },
  positionLabel: {
    display: 'block',
    color: '#757676',
  },
}));

const RatingPreview = ({
  questionId = -1,
  title = defaultTitle,
  titleRef,
  titleAriaLabel,
  commentAriaLabel,
  description = defaultDescription,
  iconType = ratingsSlidingIcons[0].id,
  handleAnswer = () => {},
  onCommentChange = () => {},
  savedAnswer,
  maxLabel = '',
  minLabel = '',
  maxRating = MAX_COUNT,
  allowKeyboardSelection = true,
  showIndex = true,
  showDescription = false,
  showSkipButton = false,
  showTextComment = false,
  isRequired = false,
  isActive = true,
  commentsLabel,
  commentsPlaceholder,
  translation = DEFAULT_TRANSLATION,
}) => {
  const styles = useStyles();

  const emptyAnswer = {
    value: null,
    questionId,
    comment: '',
  };

  const [iconsArray, setIcons] = useState([]);
  const [answer, setAnswer] = useState(savedAnswer || emptyAnswer);
  const [, selectAnswer] = useState(false);
  const { width: viewportWidth } = useViewport();
  const iconCount = iconsArray.length;

  const scaleContainerRef = useRef(null);

  const buttonWidth = () => {
    const containerWidth = scaleContainerRef.current ? scaleContainerRef.current.offsetWidth : 0;
    // NOTE: subtracting 1 from the result to ensure it is smaller than
    // total container width.
    const pixelWidth = Math.floor(containerWidth / iconCount) - 1;

    if (pixelWidth > MAX_WIDTH) return MAX_WIDTH;
    if (pixelWidth < MIN_WIDTH) return MIN_WIDTH;

    return pixelWidth;
  };

  const labelWidth = () => {
    return buttonWidth() * iconCount;
  };

  const getIconsFromS3 = useCallback(
    (limit, iconType) =>
      [...Array(maxRating).keys()].map((i) => {
        const iconId = i + 1;
        return getIconUrl(iconId, RATINGS_SLIDING, iconType, iconId === limit);
      }),
    [maxRating],
  );

  const reColorIcons = useCallback(
    (index) => {
      const newIcons = getIconsFromS3(index, iconType);
      setIcons(newIcons);
    },
    [iconType, getIconsFromS3],
  );

  useEffect(() => {
    // console.log('viewport width: ', viewportWidth);
  }, [viewportWidth]);

  useEffect(() => {
    if (savedAnswer && Object.entries(savedAnswer).length > 0) {
      reColorIcons(savedAnswer.value);
    } else {
      reColorIcons(-1);
    }
  }, [savedAnswer, reColorIcons]);

  const numberKeySelector = useNumberKeySelectorCallback(1, maxRating, (key) => {
    handleAnswerSelection(key);
  });

  useKeySelect(allowKeyboardSelection, numberKeySelector);

  const handleMouseEnter = (index) => {
    reColorIcons(index);
  };

  const handleMouseLeave = (answer) => {
    reColorIcons(answer.value || -1);
  };

  const handleOptionalComment = (event) => {
    const newAnswer = { ...answer, comment: event.target.value };
    setAnswer(newAnswer);
    onCommentChange(event.target.value);
  };

  const handleAnswerSelection = (index) => {
    reColorIcons(index);
    const newAnswer = {
      ...answer,
      value: answer.value === index ? null : index,
    };
    setAnswer(newAnswer);
    const hasNewValue = newAnswer.value !== null;
    selectAnswer(hasNewValue);
    handleAnswer(newAnswer, !showTextComment && hasNewValue);
  };

  const onConfirm = (hasAnswer = false) => {
    if (hasAnswer) {
      handleAnswer(answer);
    } else {
      setAnswer(emptyAnswer);
      selectAnswer(true);
      handleAnswer(emptyAnswer);
    }
  };

  return (
    <div>
      <Header
        isRequired={isRequired}
        title={title}
        titleRef={titleRef}
        titleAriaLabel={titleAriaLabel}
        description={description}
        showDescription={showDescription}
        isActive={isActive}
        questionId={questionId}
        translation={translation}
      />

      <div className={styles.scaleRoot}>
        <div className={styles.scaleContainer} ref={scaleContainerRef}>
          {iconsArray.map((iconUrl, index) => {
            const position = index + 1;
            const ariaLabel = buildRatingOptionArialLabel(translation, iconsArray, index, position, minLabel, maxLabel);
            return (
              <Button
                className={styles.scaleButton}
                key={index}
                onClick={() => handleAnswerSelection(position)}
                onMouseOver={() => handleMouseEnter(position)}
                onMouseLeave={() => handleMouseLeave(answer)}
                tabIndex={setTabIndex(isActive)}
                aria-hidden={!isActive}
                aria-label={ariaLabel}
                aria-pressed={position === answer?.value}
                style={{ width: `${buttonWidth()}px` }}
              >
                {showIndex && (
                  <Typography className={styles.positionLabel} aria-hidden align="center">
                    {position}
                  </Typography>
                )}
                <img className={styles.icon} src={iconUrl} aria-hidden alt={`${RATINGS_SLIDING}_${position}`} />
              </Button>
            );
          })}
        </div>

        <Grid container direction="row" className={styles.labelContainer} style={{ width: `${labelWidth()}px` }}>
          <Grid item xs={6}>
            {minLabel && (
              <Typography className={styles.minLabel} aria-hidden>
                {minLabel}
              </Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            {maxLabel && (
              <Typography className={styles.maxLabel} aria-hidden>
                {maxLabel}
              </Typography>
            )}
          </Grid>
        </Grid>
      </div>

      <Footer
        value={answer.value}
        showSkipButton={showSkipButton && answer.value === null}
        showOkButton={showTextComment && answer.value !== null}
        onConfirm={onConfirm}
        showTextComment={showTextComment}
        commentAriaLabel={commentAriaLabel}
        optionalComment={answer.comment}
        onChangeComment={handleOptionalComment}
        isActive={isActive}
        label={commentsLabel}
        placeholder={commentsPlaceholder}
        translation={translation}
      />
    </div>
  );
};

RatingPreview.propTypes = {
  questionId: PropTypes.number,
  title: PropTypes.string,
  iconType: PropTypes.string,
  description: PropTypes.string,
  handleAnswer: PropTypes.func,
  onCommentChange: PropTypes.func,
  savedAnswer: PropTypes.object,
  maxRating: PropTypes.number,
  maxLabel: PropTypes.string,
  minLabel: PropTypes.string,
  showDescription: PropTypes.bool,
  showSkipButton: PropTypes.bool,
  showTextComment: PropTypes.bool,
  allowKeyboardSelection: PropTypes.bool,
  isRequired: PropTypes.bool,
  isActive: PropTypes.bool,
};

export default RatingPreview;
